import axiosInstance from "./AxiosInstance"

const API_URL = "/batchchangerequests"

// lms-api error responses come in two shapes depending on the module: some
// put a plain string in `message`, this one (built on the getMessage/locale
// helper) puts an object `{code, message, ariaLabel}` there instead — and
// separately carries the actually-specific, actionable reason (e.g. "Only
// an approved request can be reverted") in `errorStack`, which is just a
// generic label. This unwraps whichever shape shows up and prefers the
// specific one, instead of ever handing a raw object to a toast/Swal call.
export const extractBatchChangeErrorMessage = (error, fallback = "Something went wrong. Please try again.") => {
  const data = error?.response?.data
  if (!data) return fallback
  if (typeof data.errorStack === "string" && data.errorStack.trim()) return data.errorStack
  const msg = data.message
  if (typeof msg === "string" && msg.trim()) return msg
  if (msg && typeof msg === "object" && typeof msg.message === "string") return msg.message
  return fallback
}

// List requests. Backend auto-scopes to the caller's own requests unless the
// caller is an admin/superadmin/crm profile — in that case it returns
// everyone's, filterable by status/courseId/studentId/fromDate/toDate, and
// "search" matches the reason text, the student's name/email, or either
// batch's name (all resolved server-side, not filtered client-side).
export const getBatchChangeRequests = async ({
  page = 1,
  limit = 10,
  search = "",
  searchField = "",
  status = "",
  courseId = "",
  studentId = "",
  fromDate = "",
  toDate = "",
} = {}) => {
  try {
    const params = { page, limit }
    if (search && search.trim() !== "") {
      params.search = search.trim()
      if (searchField && searchField !== "all") params.searchField = searchField
    }
    if (status) params.status = status
    if (courseId) params.courseId = courseId
    if (studentId) params.studentId = studentId
    if (fromDate) params.fromDate = fromDate
    if (toDate) params.toDate = toDate

    const response = await axiosInstance.get(API_URL, { params })
    return {
      data: response.data.data || [],
      pagination: response.data.pagination,
      // { all, pending, approved, rejected, cancelled, reverted } over the
      // caller's whole scope -- the /batch-requests tiles and tab badges.
      counts: response.data.meta?.counts || null,
    }
  } catch (error) {
    console.error("Error fetching batch change requests", error)
    throw error
  }
}

export const getBatchChangeRequestById = async (id) => {
  try {
    const response = await axiosInstance.get(`${API_URL}/${id}`)
    return response.data
  } catch (error) {
    console.error("Error fetching batch change request details", error)
    throw error
  }
}

export const createBatchChangeRequest = async (payload) => {
  try {
    const response = await axiosInstance.post(API_URL, payload)
    return response.data
  } catch (error) {
    console.error("Error creating batch change request", error)
    throw error
  }
}

export const updateBatchChangeRequest = async (id, payload) => {
  try {
    const response = await axiosInstance.put(`${API_URL}/${id}`, payload)
    return response.data
  } catch (error) {
    console.error("Error updating batch change request", error)
    throw error
  }
}

export const deleteBatchChangeRequest = async (id) => {
  try {
    const response = await axiosInstance.delete(`${API_URL}/${id}`)
    return response.data
  } catch (error) {
    console.error("Error deleting batch change request", error)
    throw error
  }
}

// Student withdraws their own still-pending request.
export const cancelBatchChangeRequest = async (id) => {
  try {
    const response = await axiosInstance.put(`${API_URL}/${id}/cancel`)
    return response.data
  } catch (error) {
    console.error("Error cancelling batch change request", error)
    throw error
  }
}

// Approver rejects a pending request.
export const rejectBatchChangeRequest = async (id, rejectionReason = "") => {
  try {
    const response = await axiosInstance.put(`${API_URL}/${id}/reject`, { rejectionReason })
    return response.data
  } catch (error) {
    console.error("Error rejecting batch change request", error)
    throw error
  }
}

// Approver approves a pending request — moves the student between batches.
export const approveBatchChangeRequest = async (id) => {
  try {
    const response = await axiosInstance.put(`${API_URL}/${id}/approve`)
    return response.data
  } catch (error) {
    console.error("Error approving batch change request", error)
    throw error
  }
}

// Approver undoes a MISTAKEN approval — swaps the student back to their
// original batch. Only works while the request is still 'approved' and the
// student's still actually in the batch it moved them into (state may have
// drifted since, in which case this is rejected rather than corrupting data).
export const revertBatchChangeRequest = async (id) => {
  try {
    const response = await axiosInstance.put(`${API_URL}/${id}/revert`)
    return response.data
  } catch (error) {
    console.error("Error reverting batch change request", error)
    throw error
  }
}
